import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { map, of, switchMap } from 'rxjs';

import {
  DynamicField,
  DynamicFieldAction,
  DynamicFieldRule,
  DynamicFormDefinition,
  DynamicLayoutSection,
} from '@models/dynamic-form.interface';
import { DynamicFormsService } from '@services/dynamic-forms.service';

/** Action types available in the rule builder UI */
export const RULE_ACTION_TYPES = [
  'show',
  'hide',
  'enable',
  'disable',
  'setValue',
  'calculate',
  'setRequired',
  'loadOptions',
] as const;

@Component({
  selector: 'cvx-dynamic-form-builder-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './dynamic-form-builder-page.component.html',
  styleUrl: './dynamic-form-builder-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFormBuilderPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(DynamicFormsService);
  private readonly fb = inject(FormBuilder);

  private readonly routeId = toSignal(this.route.paramMap.pipe(map((params) => params.get('id'))), { initialValue: null });
  protected readonly isCreateMode = computed(() => !this.routeId());
  protected readonly submitAttempted = signal(false);

  protected readonly baseForm = toSignal(
    this.route.paramMap.pipe(
      map((params) => params.get('id')),
      switchMap((id) => (id ? this.service.getById(id) : of(this.service.createDraft())))
    ),
    { initialValue: this.service.createDraft() }
  );

  protected readonly builderForm = this.fb.group({
    id: ['', Validators.required],
    name: ['', [Validators.required, Validators.minLength(2)]],
    description: [''],
    pageTitle: ['', Validators.required],
    testTypeId: [''],
    layoutType: this.fb.control<'vertical' | 'grid'>('vertical', { nonNullable: true }),
    fields: this.fb.array<FormGroup>([]),
    sections: this.fb.array<FormGroup>([]),
  });

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => params.get('id')),
        switchMap((id) => (id ? this.service.getById(id) : of(this.service.createDraft())))
      )
      .subscribe((definition) => {
        const value = definition ?? this.service.createDraft();
        this.apply(value);
      });
  }

  protected get fieldsArray(): FormArray<FormGroup> {
    return this.builderForm.controls.fields;
  }

  protected get sectionsArray(): FormArray<FormGroup> {
    return this.builderForm.controls.sections;
  }

  protected addField(): void {
    const id = `field-${Math.random().toString(36).slice(2, 8)}`;
    const name = `field_${Math.random().toString(36).slice(2, 8)}`;
    this.fieldsArray.push(
      this.createFieldGroup({
        id,
        name,
        label: 'New Field',
        type: 'text',
      })
    );

    if (!this.sectionsArray.length) {
      this.addSection();
    }

    const firstSection = this.sectionsArray.at(0);
    const fieldIds = [...(firstSection.controls['fieldIds'].value as string[]), id];
    firstSection.controls['fieldIds'].setValue(fieldIds);
  }

  protected removeField(index: number): void {
    const fieldId = this.fieldsArray.at(index).controls['id'].value;
    this.fieldsArray.removeAt(index);

    for (const section of this.sectionsArray.controls) {
      const fieldIds = (section.controls['fieldIds'].value as string[]).filter((id) => id !== fieldId);
      section.controls['fieldIds'].setValue(fieldIds);
    }
  }

  // ─── Rules & Actions (drives calculated / conditional fields) ─────────────

  protected readonly actionTypes = RULE_ACTION_TYPES;

  protected rulesArray(fieldIndex: number): FormArray<FormGroup> {
    return this.fieldsArray.at(fieldIndex).controls['rules'] as FormArray<FormGroup>;
  }

  protected actionsArray(fieldIndex: number, ruleIndex: number): FormArray<FormGroup> {
    return this.rulesArray(fieldIndex).at(ruleIndex).controls['actions'] as FormArray<FormGroup>;
  }

  protected otherFields(fieldIndex: number) {
    return this.fieldsArray.controls.filter((_, i) => i !== fieldIndex);
  }

  /**
   * All fields a rule can target, including the field the rule is defined on
   * (labeled distinctly) so self-referencing rules are possible, e.g.
   * "if this field > 0, calculate this field * 2".
   */
  protected targetFieldOptions(fieldIndex: number): { id: string; label: string }[] {
    const current = this.fieldsArray.at(fieldIndex);
    const selfOption = { id: current.controls['id'].value, label: `${current.controls['label'].value} (this field)` };
    const otherOptions = this.otherFields(fieldIndex).map((f) => ({
      id: f.controls['id'].value,
      label: f.controls['label'].value,
    }));
    return [selfOption, ...otherOptions];
  }

  protected addRule(fieldIndex: number): void {
    // Default the target field to the field the rule is defined on (self-reference),
    // so the rule isn't left with an empty (invalid) selection that silently blocks saving.
    const defaultTargetFieldId = this.fieldsArray.at(fieldIndex).controls['id'].value;
    this.rulesArray(fieldIndex).push(this.createRuleGroup(undefined, defaultTargetFieldId));
  }

  protected removeRule(fieldIndex: number, ruleIndex: number): void {
    this.rulesArray(fieldIndex).removeAt(ruleIndex);
  }

  protected addAction(fieldIndex: number, ruleIndex: number): void {
    this.actionsArray(fieldIndex, ruleIndex).push(this.createActionGroup());
  }

  protected removeAction(fieldIndex: number, ruleIndex: number, actionIndex: number): void {
    this.actionsArray(fieldIndex, ruleIndex).removeAt(actionIndex);
  }

  protected moveField(index: number, direction: -1 | 1): void {
    const next = index + direction;
    if (next < 0 || next >= this.fieldsArray.length) {
      return;
    }

    const current = this.fieldsArray.at(index);
    const target = this.fieldsArray.at(next);

    this.fieldsArray.setControl(index, target);
    this.fieldsArray.setControl(next, current);
  }

  protected addSection(): void {
    this.sectionsArray.push(
      this.fb.group({
        id: [`section-${Math.random().toString(36).slice(2, 8)}`],
        title: ['New Section', Validators.required],
        columns: [1, Validators.required],
        fieldIds: this.fb.control<string[]>([]),
      })
    );
  }

  protected removeSection(index: number): void {
    this.sectionsArray.removeAt(index);
  }

  protected toggleFieldInSection(sectionIndex: number, fieldId: string): void {
    const section = this.sectionsArray.at(sectionIndex);
    const current = [...(section.controls['fieldIds'].value as string[])];
    const existingIndex = current.indexOf(fieldId);

    if (existingIndex >= 0) {
      current.splice(existingIndex, 1);
    } else {
      current.push(fieldId);
    }

    section.controls['fieldIds'].setValue(current);
  }

  protected hasField(sectionIndex: number, fieldId: string): boolean {
    const fieldIds = this.sectionsArray.at(sectionIndex).controls['fieldIds'].value as string[];
    return fieldIds.includes(fieldId);
  }

  protected save(): void {
    this.submitAttempted.set(true);

    if (this.builderForm.invalid) {
      this.builderForm.markAllAsTouched();
      return;
    }

    const source = this.baseForm() ?? this.service.createDraft();
    const value = this.builderForm.getRawValue();

    const updated: DynamicFormDefinition = {
      ...source,
      id: value.id,
      name: value.name,
      description: value.description,
      pageTitle: value.pageTitle,
      testTypeId: value.testTypeId,
      fields: this.normalizeFieldsForSave(value.fields),
      layout: {
        ...source.layout,
        type: value.layoutType,
        sections: value.sections as DynamicLayoutSection[],
        tables: source.layout?.tables ?? [],
      },
    };

    this.service.save(updated).subscribe((saved) => {
      this.router.navigate(['/tools/dynamic-forms', saved.id, 'runtime']);
    });
  }

  private apply(definition: DynamicFormDefinition): void {
    this.builderForm.patchValue({
      id: definition.id,
      name: definition.name,
      description: definition.description ?? '',
      pageTitle: definition.pageTitle,
      testTypeId: definition.testTypeId ?? '',
      layoutType: definition.layout?.type ?? 'vertical',
    });

    this.fieldsArray.clear();
    for (const field of definition.fields) {
      this.fieldsArray.push(this.createFieldGroup(field));
    }

    this.sectionsArray.clear();
    const sections = definition.layout?.sections?.length
      ? definition.layout.sections
      : [
          {
            id: 'section-main',
            title: 'Main',
            columns: 1,
            fieldIds: definition.fields.map((field) => field.id),
          },
        ];

    for (const section of sections) {
      this.sectionsArray.push(
        this.fb.group({
          id: [section.id],
          title: [section.title, Validators.required],
          columns: [section.columns, Validators.required],
          fieldIds: this.fb.control<string[]>(section.fieldIds),
        })
      );
    }
  }

  private createFieldGroup(field: DynamicField): FormGroup {
    return this.fb.group({
      id: [field.id, Validators.required],
      name: [field.name, [Validators.required, Validators.pattern(/^[a-zA-Z][a-zA-Z0-9_]*$/)]],
      label: [field.label, Validators.required],
      type: [field.type, Validators.required],
      isRequired: [field.isRequired ?? false],
      isReadonly: [field.isReadonly ?? false],
      isHidden: [field.isHidden ?? false],
      minLength: [field.minLength ?? null],
      maxLength: [field.maxLength ?? null],
      minValue: [field.minValue ?? null],
      maxValue: [field.maxValue ?? null],
      allowDecimal: [field.allowDecimal ?? false],
      decimalPlaces: [field.decimalPlaces ?? null],
      defaultValue: [field.defaultValue ?? ''],
      parentFieldId: [field.parentFieldId ?? ''],
      data: [field.data ?? []],
      cascadingData: [field.cascadingData ?? {}],
      rules: this.fb.array((field.rules ?? []).map((rule) => this.createRuleGroup(rule))),
    });
  }

  private createRuleGroup(rule?: DynamicFieldRule, defaultTargetFieldId = ''): FormGroup {
    return this.fb.group({
      targetFieldId: [rule?.targetFieldId ?? defaultTargetFieldId, Validators.required],
      condition: [rule?.condition ?? ''],
      actions: this.fb.array((rule?.actions ?? [this.defaultAction()]).map((action) => this.createActionGroup(action))),
    });
  }

  private createActionGroup(action?: DynamicFieldAction): FormGroup {
    return this.fb.group({
      type: [action?.type ?? 'calculate', Validators.required],
      // Raw text entry; setValue/setRequired values are coerced to bool/number/string on save
      value: [action?.value !== undefined ? String(action.value) : ''],
      expression: [action?.expression ?? ''],
    });
  }

  private defaultAction(): DynamicFieldAction {
    return { type: 'calculate', expression: '' };
  }

  /** Converts the raw builder-form field array into DynamicField[], coercing rule action values */
  private normalizeFieldsForSave(rawFields: unknown): DynamicField[] {
    const fields = rawFields as Array<DynamicField & { rules: Array<DynamicFieldRule & { actions: DynamicFieldAction[] }> }>;

    return fields.map((field) => ({
      ...field,
      rules: (field.rules ?? [])
        .filter((rule) => !!rule.targetFieldId)
        .map((rule) => ({
          ...rule,
          actions: rule.actions
            .filter((action) => !!action.type)
            .map((action) => ({
              type: action.type,
              expression: action.expression || undefined,
              value: this.coerceActionValue(action.value),
            })),
        })),
    }));
  }

  /** Text-input rule action values are stored as strings in the form; coerce back to bool/number/string */
  private coerceActionValue(raw: unknown): unknown {
    if (raw === '' || raw === undefined || raw === null) return undefined;
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    const num = Number(raw);
    return raw !== '' && !Number.isNaN(num) ? num : raw;
  }
}
